/**
 * Photos Store — Thumbnail cache + upload queue with offline support
 *
 * Thumbnails are cached in IndexedDB as data URLs.
 * Uploads go directly to the backend when online, or into a queue when offline.
 * The queue is processed by useAutoSync on reconnect.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { db } from '@/db'
import { photosApi, type UploadProgressCallback } from '@/api/photos.api'
import type { PhotoThumbnail, PhotoUploadQueueItem } from '@/types/local.types'

const THUMBNAIL_STALE_MS = 24 * 60 * 60_000 // 24 hours
const MAX_RETRY_COUNT = 3

/** Convert a data URL string to a Blob. */
function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',')
  const mime = parts[0]?.match(/:(.*?);/)?.[1] ?? 'image/jpeg'
  const binary = atob(parts[1] ?? '')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/** Convert a Blob to a data URL string. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Generate a local thumbnail from a data URL (for offline preview).
 * Resizes to max 128px, JPEG at 80% quality.
 */
function generateLocalThumbnail(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const MAX = 128
      let { width, height } = img
      if (width > MAX || height > MAX) {
        if (width >= height) {
          height = Math.round((height * MAX) / width)
          width = MAX
        } else {
          width = Math.round((width * MAX) / height)
          height = MAX
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return reject(new Error('Canvas 2D unavailable'))
      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/jpeg', 0.8))
    }
    img.onerror = () => reject(new Error('Failed to decode image'))
    img.src = dataUrl
  })
}

export const usePhotosStore = defineStore('photos', () => {
  // ── State ───────────────────────────────────────────────────────────────────
  /** In-memory thumbnail cache: personId → data URL */
  const thumbnails = ref<Map<number, string>>(new Map())
  /** Set of personIds with pending uploads in queue */
  const pendingUploads = ref<Set<number>>(new Set())

  // ── Getters ─────────────────────────────────────────────────────────────────
  const getThumbnailUrl = computed(() => {
    return (personId: number): string | null => thumbnails.value.get(personId) ?? null
  })

  const hasPendingUpload = computed(() => {
    return (personId: number): boolean => pendingUploads.value.has(personId)
  })

  const pendingCount = computed(() => pendingUploads.value.size)

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * Load a thumbnail for a person.
   * 1. Check in-memory cache
   * 2. Check IndexedDB cache (if fresh, use it)
   * 3. Fetch from backend (if online)
   */
  async function loadThumbnail(personId: number): Promise<void> {
    // Already in memory
    if (thumbnails.value.has(personId)) return

    // Check IndexedDB cache
    const cached = await db.photoThumbnails.get(personId)
    if (cached) {
      thumbnails.value.set(personId, cached.dataUrl)

      // Background refresh if stale + online
      const age = Date.now() - new Date(cached.fetchedAt).getTime()
      if (age > THUMBNAIL_STALE_MS && navigator.onLine) {
        fetchThumbnailFromBackend(personId, cached.etag).catch(() => {})
      }
      return
    }

    // Fetch from backend if online
    if (navigator.onLine) {
      await fetchThumbnailFromBackend(personId)
    }
  }

  /**
   * Fetch thumbnail from backend and update cache.
   * Uses ETag for conditional requests (304 = no change).
   */
  async function fetchThumbnailFromBackend(personId: number, etag?: string): Promise<void> {
    try {
      const result = await photosApi.getThumbnail(personId, etag)

      // 304 Not Modified — just update fetchedAt
      if (result.blob.size === 0 && etag) {
        await db.photoThumbnails.update(personId, { fetchedAt: new Date().toISOString() })
        return
      }

      const dataUrl = await blobToDataUrl(result.blob)
      const entry: PhotoThumbnail = {
        personId,
        dataUrl,
        etag: result.etag,
        fetchedAt: new Date().toISOString(),
      }
      await db.photoThumbnails.put(entry)
      thumbnails.value.set(personId, dataUrl)
    } catch {
      // Silently fail — thumbnail fetch errors don't need user notification
    }
  }

  /**
   * Upload a photo for a person.
   * Accepts data URL (from CameraDialog) or File/Blob (from file input).
   * Online: direct upload + refresh thumbnail.
   * Offline: queue in IndexedDB + generate local thumbnail.
   */
  async function uploadPhoto(
    personId: number,
    source: string | File | Blob,
    onProgress?: UploadProgressCallback
  ): Promise<'uploaded' | 'queued'> {
    // Normalize to data URL for queue storage, and Blob for API upload
    let dataUrl: string
    let blob: Blob

    if (typeof source === 'string') {
      dataUrl = source
      blob = dataUrlToBlob(source)
    } else {
      blob = source
      dataUrl = await blobToDataUrl(source)
    }

    if (navigator.onLine) {
      // Direct upload
      await photosApi.upload(personId, blob, onProgress)
      // Immediately show uploaded image as local thumbnail (don't depend on backend roundtrip)
      try {
        const localThumb = await generateLocalThumbnail(dataUrl)
        const entry: PhotoThumbnail = {
          personId,
          dataUrl: localThumb,
          fetchedAt: new Date().toISOString(),
        }
        await db.photoThumbnails.put(entry)
        thumbnails.value.set(personId, localThumb)
      } catch {
        thumbnails.value.set(personId, dataUrl)
      }
      // Background: replace with backend-generated thumbnail (proper ETag etc.)
      fetchThumbnailFromBackend(personId).catch(() => {})
      return 'uploaded'
    } else {
      // Queue for later
      const item: PhotoUploadQueueItem = {
        personId,
        dataUrl,
        createdAt: new Date().toISOString(),
        retryCount: 0,
      }
      await db.photoUploadQueue.add(item)
      pendingUploads.value.add(personId)

      // Generate local thumbnail for immediate display
      try {
        const localThumb = await generateLocalThumbnail(dataUrl)
        const entry: PhotoThumbnail = {
          personId,
          dataUrl: localThumb,
          fetchedAt: new Date().toISOString(),
        }
        await db.photoThumbnails.put(entry)
        thumbnails.value.set(personId, localThumb)
      } catch {
        // Fallback: use the full image as thumbnail (larger but works)
        thumbnails.value.set(personId, dataUrl)
      }
      return 'queued'
    }
  }

  /**
   * Delete a person's photo. Online-only.
   */
  async function deletePhoto(personId: number): Promise<void> {
    await photosApi.delete(personId)
    await db.photoThumbnails.delete(personId)
    thumbnails.value.delete(personId)
  }

  /**
   * Process the upload queue. Called by useAutoSync on reconnect.
   * Returns sync result counts.
   */
  async function syncQueue(): Promise<{ success: number; failed: number }> {
    const items = await db.photoUploadQueue.toArray()
    let success = 0
    let failed = 0

    for (const item of items) {
      try {
        const blob = dataUrlToBlob(item.dataUrl)
        await photosApi.upload(item.personId, blob)
        // Upload succeeded — remove from queue
        await db.photoUploadQueue.delete(item.id!)
        pendingUploads.value.delete(item.personId)
        // Refresh thumbnail from backend (replaces local one)
        await fetchThumbnailFromBackend(item.personId)
        success++
      } catch (e) {
        failed++
        const newRetry = (item.retryCount ?? 0) + 1
        const errorMsg = e instanceof Error ? e.message : 'Unknown error'
        if (newRetry >= MAX_RETRY_COUNT) {
          // Give up — remove from queue
          await db.photoUploadQueue.delete(item.id!)
          pendingUploads.value.delete(item.personId)
        } else {
          await db.photoUploadQueue.update(item.id!, {
            retryCount: newRetry,
            lastError: errorMsg,
          })
        }
      }
    }

    return { success, failed }
  }

  /**
   * Initialize pending uploads set from IndexedDB (called once at startup).
   */
  async function initPendingUploads(): Promise<void> {
    const items = await db.photoUploadQueue.toArray()
    const newSet = new Set<number>()
    for (const item of items) newSet.add(item.personId)
    pendingUploads.value = newSet
  }

  function $reset() {
    thumbnails.value = new Map()
    pendingUploads.value = new Set()
  }

  return {
    // State (exposed for reactivity)
    thumbnails,
    pendingUploads,
    // Getters
    getThumbnailUrl,
    hasPendingUpload,
    pendingCount,
    // Actions
    loadThumbnail,
    uploadPhoto,
    deletePhoto,
    syncQueue,
    initPendingUploads,
    $reset,
  }
})
